import { Box, Button, Tooltip } from "@deere/fuel-react";
import { AddCircle, Check, Delete, RemoveCircle } from "@mui/icons-material";
import GetAppIcon from '@mui/icons-material/GetApp';
import { IconButton } from "@mui/material";
import {
  GridRowModes,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton
} from "@mui/x-data-grid";
import { randomId } from "../../utils/random-id";
import { useSelector } from "react-redux";
import { APP_VIEW, EXTENSION_EXCEL_COLUMNS, EXTENSION_EXCEL_COLUMNS_LOGS, ROLES } from "../../constants/constant";
import { getUserRole } from "../../utils/utils";
import Banner from "../Banner";
import useExcelExport from "../Hooks/useExcelExport";

const GridToolbar = ({
  isInEditMode,
  handleSave,
  handleCancel,
  handleEditClick,
  handleLockClick,
  handleUnLockClick,
  isLocked,
  editableAndFreezable,
  isUnlocked,
  view,
  isAllExpanded,
  handleExpandAll,
  disableExpandAll,
  setRows,
  setIsInEditMode,
  setRowModesModel,
  showToast,
  dmView,
  count,
  setEditedRow,
  columns,
  fileName,
  confirmSelectedManualRequests,
  isConfirmingManualRequests,
  deleteSelectedManualRequests,
  isDeletingManualRequests,
  manualRequestSelectedCount = 0,
}) => {
  const userData = useSelector((state) => state.userState.userData);
  const { exportToExcel } = useExcelExport();
  const excelData = useSelector((state) => state.excelState.extensionRequests);
  const toolbarBKP = () => {
    return (
      <div>
        {view === APP_VIEW.IB_BP ? (
          <div style={{ paddingInline: "10px" }}>
            <Banner
              bannerMsg="For any unchecked items, please contact with the respective CCM for
            assistance and further action."
            />
          </div>
        ) : null}
        <GridToolbarContainer className="custom-toolbar">
          <Box
            sx={{
              p: 1,
              m: 1,
            }}
          >
            {view === APP_VIEW.BKP && getUserRole(userData) === ROLES.BKP ? (
              isInEditMode ? (
                <div>
                  <Button variant="primary" sx={{ ml: 1 }} onClick={handleSave}>
                    Save
                  </Button>
                  <Button variant="primary" sx={{ ml: 1 }} onClick={handleCancel}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <div>
                  <Button
                    variant="primary"
                    sx={{ ml: 1 }}
                    onClick={handleEditClick}
                    disabled={isLocked || !editableAndFreezable}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="primary"
                    sx={{ ml: 1 }}
                    onClick={handleLockClick}
                    disabled={isLocked || !editableAndFreezable}
                  >
                    Lock
                  </Button>
                </div>
              )
            ) :null
          }
          </Box>
        </GridToolbarContainer>
      </div>
    );
  };

  const extToolbar = () => {
    const handleExcelExport = () => {
      if (!excelData || excelData.length === 0) {
        if (showToast) {
          showToast('No data available to export', 'error');
        }
        return;
      }
  const columnsToExport = view === APP_VIEW.LOGS ? EXTENSION_EXCEL_COLUMNS.concat(EXTENSION_EXCEL_COLUMNS_LOGS) : EXTENSION_EXCEL_COLUMNS;
      exportToExcel(excelData, columnsToExport, fileName || 'extension_requests');
    };

    return (
      <GridToolbarContainer>
        {!disableExpandAll && (
          <Tooltip title="Expand All">
            <IconButton onClick={handleExpandAll}>
              {isAllExpanded ? (
                <RemoveCircle color="primary" sx={{ fontSize: 30 }} />
              ) : (
                <AddCircle color="primary" sx={{ fontSize: 30 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <Tooltip title="Export to Excel">
          <IconButton onClick={handleExcelExport}>
            <GetAppIcon style={{ color: 'green' }} />
          </IconButton>
        </Tooltip>
        {(confirmSelectedManualRequests || deleteSelectedManualRequests) && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {confirmSelectedManualRequests && (
              <Tooltip title="Approve selected requests">
                <IconButton
                  onClick={confirmSelectedManualRequests}
                  disabled={isConfirmingManualRequests || isDeletingManualRequests}
                >
                  <Check color="success" />
                </IconButton>
              </Tooltip>
            )}
            {deleteSelectedManualRequests && (
              <Tooltip title={`Delete selected requests (${manualRequestSelectedCount})`}>
                <span>
                  <IconButton
                    onClick={deleteSelectedManualRequests}
                    disabled={isDeletingManualRequests || isConfirmingManualRequests}
                    aria-label="delete selected manual requests"
                  >
                    <Delete color="error" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
        )}
      </GridToolbarContainer>
    );
  };

  const dmToolbar = () => {
    const addProduct = () => {
      setRows((prevRows) => {
        if (prevRows.some(row => row.isNew)) {
          showToast && showToast('Please save or cancel the current new product before adding another.', 'warning');
          return prevRows;
        }
        const id = randomId();
        const newRow = { id, baseCode: "", materialType: "", isNew: true };
        setIsInEditMode(true);
        setRowModesModel({ [id]: { mode: GridRowModes.Edit } });
        setEditedRow(newRow.id);
        return [newRow, ...prevRows];
      });
    };
    const addDealer = () => {
      setRows((prevRows) => {
        if (prevRows.some(row => row.isNew)) {
          showToast && showToast('Please save or cancel the current new dealer before adding another.', 'warning');
          return prevRows;
        }
        const id = randomId();
        const newRow = {
          id,
          customerNumber: "",
          email: "",
          country: "",
          highRiskDealer: "",
          isNew: true,
        };
        setIsInEditMode(true);
        setRowModesModel({[id]: { mode: GridRowModes.Edit } });
        setEditedRow(newRow.id);
        return [newRow, ...prevRows];
      });
    };
    const addMachine = () => {
      setRows((prevRows) => {
        if (prevRows.some(row => row.isNew)) {
          showToast && showToast('Please save or cancel the current new machine before adding another.', 'warning');
          return prevRows;
        }
        const id = randomId();
        const newRow = { id, serialNo: "", comments: "", isNew: true };
        setIsInEditMode(true);
        setRowModesModel({ [id]: { mode: GridRowModes.Edit } });
        setEditedRow(newRow.id);
        return [newRow, ...prevRows];
      });
    };
    const addDirectDebitDealer = () => {
      setRows((prevRows) => {
        if (prevRows.some(row => row.isNew)) {
          showToast && showToast('Please save or cancel the current new direct debit dealer before adding another.', 'warning');
          return prevRows;
        }
        const id = randomId();
        const newRow = {
          id,
          directDebitCustomerNumber: "",
          currency: "",
          country: "JD",
          isNew: true,
        };
        setIsInEditMode(true);
        setRowModesModel({[id]: { mode: GridRowModes.Edit } });
        setEditedRow(newRow.id);
        return [newRow, ...prevRows];
      });
    };
    const handleAddClick = () => {
      if (dmView === "products") addProduct();
      else if (dmView === "dealers") addDealer();
      else if (dmView === "machines") addMachine();
      else if (dmView === "directDebitDealers") addDirectDebitDealer();
    };
    return (
      <GridToolbarContainer
        sx={{ display: "flex", justifyContent: "space-between" }}
      >
        <Box sx={{ p: 1, m: 1 }}>
          {dmView === "dealers" ? (
            <h2>Dealers ({count})</h2>
          ) : dmView === "products" ? (
            <h2>Products ({count})</h2>
          ) : dmView === "machines" ?          
          (
            <h2>Machines ({count})</h2>
          ) : dmView === "directDebitDealers" ? (
            <h2>JDWIN dealers on Direct Debit ({count})</h2>
          ) : (
            <h2>Users ({count})</h2>
          )}
        </Box>
        <Box sx={{ p: 1, m: 1 }}>
          <GridToolbarFilterButton />
          {( dmView === "dealers" || dmView === "products" || dmView === "machines" || dmView === "directDebitDealers") && (
          <Button variant="primary" onClick={handleAddClick}>
            {dmView === "dealers"
              ? "Add Dealer"
              : dmView === "products"
              ? "Add Product"
              : dmView === "machines"
              ? "Add Machine"
              : "Add Direct Debit Dealer"}
          </Button>
          )}
        </Box>
      </GridToolbarContainer>
    );
  };

  return (
    <div>
      {view === APP_VIEW.BKP || view === APP_VIEW.IB_BP
        ? toolbarBKP()
        : view === APP_VIEW.BP ||
          view === APP_VIEW.CCM ||
          view === APP_VIEW.ERRORS ||
          view === APP_VIEW.LOGS
        ? extToolbar()
        : view === APP_VIEW.DM
        ? dmToolbar()
        : null}
    </div>
  );
};

export default GridToolbar;
